import './style.modules.scss'

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { SkipLink } from './SkipLink'

export type SkipLinksProps = {
  showContactUs?: 'true' | ''
  testId?: string
}

export type SkipLinkType = {
  id: string
  href: string
  label: string
  targetSelector?: string
  focusSelector?: string | (() => void)
  shouldFocusOnMount?: boolean
  key?: string
}

export const SkipLinks = (props: SkipLinksProps) => {
  const { testId, showContactUs } = props
  const [skipLinks, setSkipLinks] = useState<SkipLinkType[]>([])
  const [isTabbing, setIsTabbing] = useState(false)

  const possibleSkipLinks: SkipLinkType[] = useMemo(
    () => [
      {
        id: 'skipToMainContent',
        href: '#main',
        label: 'Skip to main content',
        targetSelector: 'main',
        focusSelector: 'main a'
      },
      {
        id: 'skipToLogin',
        href: '#login',
        label: 'Skip to login',
        targetSelector: '.LoginClient',
        focusSelector: '.LoginClient input'
      },
      {
        id: 'skipToAdvisor',
        href: '#advisor',
        label: 'Skip to Find An Advisor',
        targetSelector: '[data-is="DynamicAdvisor"]',
        focusSelector: '[data-is="DynamicAdvisor"] input'
      },
      {
        id: 'skipToResults',
        href: '#results',
        label: 'Skip to Results',
        targetSelector: '#ResourceCenterResults',
        focusSelector: '#ResourceCenterResults .results .Card'
      },
      {
        id: 'skipToContactUs',
        href: '/customer-service/contact',
        label: 'Navigate to Contact Us'
      },
      {
        id: 'skipToFooter',
        href: '#footer',
        label: 'Skip to footer',
        targetSelector: 'footer.footer',
        focusSelector: () => {
          window.dispatchEvent(new CustomEvent('skipfocus', { detail: 'a' }))
        }
      }
    ],
    []
  )

  const generateSkipLinks = useCallback(
    (show: boolean, e?: KeyboardEvent) => {
      const presentSkipLinks = possibleSkipLinks.filter(currItem => {
        const { targetSelector } = currItem
        if (targetSelector) {
          if (document.querySelector(targetSelector)) {
            return true
          }
          return false
        }
        if (!showContactUs) {
          return false
        }
        return true
      })

      let focusIndex = 0
      // @ts-expect-error related target is not always present
      if (e?.relatedTarget) {
        focusIndex = presentSkipLinks.length - 1
      }

      const newSkipLinks = presentSkipLinks.map((currItem, index) => {
        return {
          id: currItem.id,
          href: currItem.href,
          index: index + 1,
          targetSelector: currItem.targetSelector,
          focusSelector: currItem.focusSelector,
          label: currItem.label,
          shouldFocusOnMount: show && focusIndex === index,
          key: currItem.label
        }
      })
      setSkipLinks(newSkipLinks)
    },
    [possibleSkipLinks, showContactUs]
  )

  useEffect(() => {
    const handleTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        generateSkipLinks(false, event)
        setIsTabbing(true)
      }
    }

    window.addEventListener('keydown', handleTab)

    return () => {
      window.removeEventListener('keydown', handleTab)
    }
  }, [isTabbing, possibleSkipLinks, generateSkipLinks])

  useEffect(() => {
    generateSkipLinks(false)
  }, [generateSkipLinks])

  return (
    <nav
      role="navigation"
      aria-label="Skip Links"
      className="u-posAbsolute u-sizeFull"
      data-testid={testId ?? null}
    >
      {skipLinks.map(currItem => (
        <SkipLink
          id={currItem.id}
          href={currItem.href}
          targetSelector={currItem.targetSelector!}
          focusSelector={currItem.focusSelector!}
          label={currItem.label}
          shouldFocusOnMount={currItem.shouldFocusOnMount}
          key={currItem.label}
        />
      ))}
    </nav>
  )
}
